// src/pages/finance/Promotions.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  CardGiftcard as GiftIcon,
  CheckCircle as CheckIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Laptop as PortalIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoAwesomeIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';

const PromotionForm = ({ open, onClose, onSubmit, billingServices, internetPlans = [], promotion = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'discount',
    discount_amount: '',
    discount_months: '3',
    speed_upgrade_multiplier: '2.0',
    speed_upgrade_price_addon: '10.00',
    service_id: '',
    target_plan_id: '',
    min_punctual_payments: '3',
    ...promotion
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        description: '',
        type: 'discount',
        discount_amount: '',
        discount_months: '3',
        speed_upgrade_multiplier: '2.0',
        speed_upgrade_price_addon: '10.00',
        service_id: '',
        target_plan_id: '',
        min_punctual_payments: '3',
        ...promotion
      });
    }
  }, [open, promotion]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '24px', p: 1 }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{promotion ? 'Editar Promoción' : 'Nueva Promoción de Fidelidad'}</span>
          <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField
              fullWidth
              label="Nombre de la Promoción"
              name="name"
              placeholder="Ej. Descuento Pago Puntual S/10, Doble Velocidad..."
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              placeholder="Detalle los beneficios para el cliente..."
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
              <InputLabel>Tipo de Promoción</InputLabel>
              <Select
                label="Tipo de Promoción"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <MenuItem value="discount">Descuento en Factura 💰</MenuItem>
                <MenuItem value="speed_upgrade">Doble Velocidad ⚡</MenuItem>
                <MenuItem value="free_service">Servicio Adicional Gratis (Streaming) 📺</MenuItem>
                <MenuItem value="speed_and_free_service">Doble Velocidad + Streaming Gratis ⚡📺</MenuItem>
                <MenuItem value="plan_upgrade_with_discount_and_free_service">Upgrade Plan + Descuento + Streaming 🚀💰📺</MenuItem>
              </Select>
            </FormControl>

            {formData.type === 'plan_upgrade_with_discount_and_free_service' && (
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                <InputLabel>Plan de Internet Destino</InputLabel>
                <Select
                  label="Plan de Internet Destino"
                  name="target_plan_id"
                  value={formData.target_plan_id}
                  onChange={handleChange}
                  required
                >
                  {internetPlans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} (S/ {parseFloat(plan.price).toFixed(2)} - {plan.download_speed}M)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(formData.type === 'free_service' || formData.type === 'speed_and_free_service' || formData.type === 'plan_upgrade_with_discount_and_free_service') && (
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                <InputLabel>Servicio Adicional de Streaming</InputLabel>
                <Select
                  label="Servicio Adicional de Streaming"
                  name="service_id"
                  value={formData.service_id}
                  onChange={handleChange}
                  required
                >
                  {billingServices.map(srv => (
                    <MenuItem key={srv.id} value={srv.id}>
                      {srv.name} (S/ {parseFloat(srv.price).toFixed(2)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(formData.type === 'discount' || formData.type === 'plan_upgrade_with_discount_and_free_service') && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Monto del Descuento (S/)"
                    name="discount_amount"
                    type="number"
                    inputProps={{ step: '0.01', min: '0.01' }}
                    value={formData.discount_amount}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Duración (Meses)"
                    name="discount_months"
                    type="number"
                    inputProps={{ min: '1' }}
                    value={formData.discount_months}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
              </Grid>
            )}

            {(formData.type === 'speed_upgrade' || formData.type === 'speed_and_free_service') && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Multiplicador de Velocidad"
                    name="speed_upgrade_multiplier"
                    type="number"
                    inputProps={{ step: '0.1', min: '1.1' }}
                    placeholder="Ej. 2.0"
                    value={formData.speed_upgrade_multiplier}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Costo Adicional (S/)"
                    name="speed_upgrade_price_addon"
                    type="number"
                    inputProps={{ step: '0.01', min: '0.00' }}
                    placeholder="Ej. 10.00"
                    value={formData.speed_upgrade_price_addon}
                    onChange={handleChange}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
              </Grid>
            )}

            <TextField
              fullWidth
              label="Meses Consecutivos de Pago Puntual Requeridos"
              name="min_punctual_payments"
              type="number"
              inputProps={{ min: '1' }}
              value={formData.min_punctual_payments}
              onChange={handleChange}
              required
              helperText="El cliente debe pagar sus facturas antes del corte por estos meses consecutivos para calificar."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              px: 4,
              fontWeight: 700,
              backgroundColor: '#6366f1',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              '&:hover': { backgroundColor: '#4f46e5' }
            }}
          >
            Guardar Promoción
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const OfferDialog = ({ open, onClose, onSubmit, selectedClients, promotion }) => {
  const [channels, setChannels] = useState({
    portal: true,
    email: true,
    whatsapp: true
  });
  const [testMode, setTestMode] = useState(true); // Modo de prueba activo por defecto
  const [submitting, setSubmitting] = useState(false);

  const handleChannelChange = (e) => {
    const { name, checked } = e.target;
    setChannels(prev => ({ ...prev, [name]: checked }));
  };

  const handleSend = async () => {
    setSubmitting(true);
    await onSubmit({
      clientIds: selectedClients.map(c => c.id),
      channels,
      testMode
    });
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Ofrecer Promoción</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Estás a punto de enviar la oferta <strong>"{promotion?.name}"</strong> a los clientes seleccionados.
          </Typography>
          
          <Paper sx={{ p: 2, bgcolor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '12px' }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={testMode} 
                  onChange={(e) => setTestMode(e.target.checked)} 
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                    Modo de Prueba (Solo Jean Castillo)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#b45309', display: 'block' }}>
                    Si está activo, las notificaciones se enviarán únicamente al cliente de prueba Jean Castillo para garantizar la seguridad.
                  </Typography>
                </Box>
              }
            />
          </Paper>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
              Canales de Notificación:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={channels.portal} onChange={handleChannelChange} name="portal" color="primary" />}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PortalIcon size="small" sx={{ color: '#475569' }} />
                    <Typography variant="body2">Portal del Cliente (Notificación interna)</Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={channels.email} onChange={handleChannelChange} name="email" color="primary" />}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon size="small" sx={{ color: '#475569' }} />
                    <Typography variant="body2">Correo Electrónico (Email de fidelización)</Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={channels.whatsapp} onChange={handleChannelChange} name="whatsapp" color="primary" />}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WhatsAppIcon size="small" sx={{ color: '#475569' }} />
                    <Typography variant="body2">WhatsApp (Mensaje directo de WhatsApp)</Typography>
                  </Stack>
                }
              />
            </FormGroup>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={submitting || (!channels.portal && !channels.email && !channels.whatsapp)}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            px: 3,
            fontWeight: 700,
            backgroundColor: '#10b981',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#059669' }
          }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Enviar Oferta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [billingServices, setBillingServices] = useState([]);
  const [internetPlans, setInternetPlans] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  
  // States for Eligible Clients tab
  const [selectedPromoForEligible, setSelectedPromoForEligible] = useState('');
  const [eligibleClients, setEligibleClients] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [openOfferDialog, setOpenOfferDialog] = useState(false);
  
  // States for Active Client Promotions tab
  const [activeClientPromos, setActiveClientPromos] = useState([]);
  const [loadingActiveClientPromos, setLoadingActiveClientPromos] = useState(false);

  // Delete promo
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);

  useEffect(() => {
    fetchPromotions();
    fetchBillingServices();
    fetchInternetPlans();
    fetchActiveClientPromos();
  }, []);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/promotions', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setPromotions(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0 && !selectedPromoForEligible) {
        setSelectedPromoForEligible(data[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      setLoading(false);
    }
  };

  const fetchBillingServices = async () => {
    try {
      const response = await fetch('/api/finance/billing-services', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setBillingServices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching billing services:', error);
    }
  };

  const fetchInternetPlans = async () => {
    try {
      const response = await fetch('/api/plans', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setInternetPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching internet plans:', error);
    }
  };

  const fetchEligibleClients = async (promoId) => {
    if (!promoId) return;
    setLoadingEligible(true);
    setSelectedClients([]);
    try {
      const response = await fetch(`/api/promotions/eligible-clients/${promoId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setEligibleClients(Array.isArray(data) ? data : []);
      setLoadingEligible(false);
    } catch (error) {
      console.error('Error fetching eligible clients:', error);
      setLoadingEligible(false);
    }
  };

  const fetchActiveClientPromos = async () => {
    setLoadingActiveClientPromos(true);
    try {
      const response = await fetch('/api/promotions/active-client-promotions', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setActiveClientPromos(Array.isArray(data) ? data : []);
      setLoadingActiveClientPromos(false);
    } catch (error) {
      console.error('Error fetching active client promos:', error);
      setLoadingActiveClientPromos(false);
    }
  };

  useEffect(() => {
    if (tabIndex === 1 && selectedPromoForEligible) {
      fetchEligibleClients(selectedPromoForEligible);
    } else if (tabIndex === 2) {
      fetchActiveClientPromos();
    }
  }, [tabIndex, selectedPromoForEligible]);

  const handlePromoSubmit = async (formData) => {
    try {
      const method = selectedPromo ? 'PUT' : 'POST';
      const url = selectedPromo ? `/api/promotions/${selectedPromo.id}` : '/api/promotions';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Error al guardar promoción');
      }

      fetchPromotions();
      setOpenForm(false);
      setSelectedPromo(null);
    } catch (error) {
      alert(error.message || 'Error al guardar la promoción');
    }
  };

  const handleOfferPromo = async ({ clientIds, channels, testMode }) => {
    try {
      const response = await fetch('/api/promotions/offer-to-client', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientIds,
          promotionId: selectedPromoForEligible,
          channels,
          testMode
        })
      });

      if (!response.ok) {
        throw new Error('Error al enviar la oferta');
      }

      const resData = await response.json();
      alert('Promoción enviada exitosamente. ' + (testMode ? '(Enviado en Modo de Prueba a Jean Castillo)' : ''));
      setOpenOfferDialog(false);
      fetchEligibleClients(selectedPromoForEligible);
    } catch (error) {
      alert(error.message || 'Error al enviar promoción');
    }
  };

  const handleDeletePromo = async () => {
    if (!promoToDelete) return;
    try {
      const response = await fetch(`/api/promotions/${promoToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Error al eliminar promoción');
      fetchPromotions();
      setDeleteDialogOpen(false);
      setPromoToDelete(null);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleClientSelect = (client) => {
    const isSelected = selectedClients.some(c => c.id === client.id);
    if (isSelected) {
      setSelectedClients(prev => prev.filter(c => c.id !== client.id));
    } else {
      setSelectedClients(prev => [...prev, client]);
    }
  };

  const handleSelectAllClients = () => {
    if (selectedClients.length === eligibleClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(eligibleClients);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Fidelización y Promociones 🎁
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mt: 0.5 }}>
            Premie la puntualidad de sus clientes con descuentos, duplicación de velocidad o perfiles de streaming gratis.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedPromo(null);
            setOpenForm(true);
          }}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            '&:hover': { backgroundColor: '#4f46e5' },
            width: { xs: '100%', sm: 'auto' },
            whiteSpace: 'nowrap'
          }}
        >
          Crear Promoción
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: '16px', mb: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newIdx) => setTabIndex(newIdx)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              minWidth: { xs: 'auto', sm: 120 },
              px: { xs: 1.5, sm: 2.5 },
              py: 1.5,
              minHeight: 44,
              whiteSpace: 'nowrap'
            }
          }}
        >
          <Tab label="Catálogo de Promociones 🎖️" />
          <Tab label="Clientes Elegibles (Pago Puntual) 🎯" />
          <Tab label="Promociones Activas de Clientes 🌟" />
        </Tabs>
      </Paper>

      {/* Tab content 1: Catalog */}
      {tabIndex === 0 && (
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : promotions.length === 0 ? (
            <Paper sx={{ p: 6, borderRadius: '20px', textAlign: 'center', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
              <GiftIcon sx={{ fontSize: 54, color: '#94a3b8', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold">No hay promociones creadas</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Crea tu primera campaña de fidelización para los clientes con buen historial de pago.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {promotions.map((promo) => (
                <Grid item xs={12} sm={6} md={4} key={promo.id}>
                  <Card sx={{
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.02)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(99, 102, 241, 0.08)',
                      borderColor: 'rgba(99, 102, 241, 0.2)'
                    }
                  }}>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Chip
                          label={
                            promo.type === 'discount' ? 'Descuento' : 
                            promo.type === 'speed_upgrade' ? 'Doble Velocidad' : 
                            promo.type === 'speed_and_free_service' ? 'Velocidad + Streaming' : 
                            promo.type === 'plan_upgrade_with_discount_and_free_service' ? 'Upgrade + Descuento + Streaming' : 'Servicio Gratis'
                          }
                          color={
                            promo.type === 'discount' ? 'success' :
                            promo.type === 'speed_upgrade' ? 'primary' : 
                            promo.type === 'speed_and_free_service' ? 'info' : 
                            promo.type === 'plan_upgrade_with_discount_and_free_service' ? 'secondary' : 'warning'
                          }
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: '6px' }}
                        />
                        <Chip
                          label={`Racha: ${promo.min_punctual_payments} meses`}
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 600, color: '#64748b', borderColor: '#e2e8f0' }}
                        />
                      </Box>

                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, fontSize: '1.05rem' }}>
                        {promo.name}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, minHeight: '40px', lineHeight: 1.5 }}>
                        {promo.description || 'Sin descripción.'}
                      </Typography>

                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        {promo.type === 'discount' && (
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Descuento Mensual:</Typography>
                              <Typography variant="subtitle2" color="success.main" fontWeight="bold">S/ {parseFloat(promo.discount_amount || 0).toFixed(2)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Duración:</Typography>
                              <Typography variant="subtitle2" color="textPrimary" fontWeight="bold">{promo.discount_months} meses</Typography>
                            </Stack>
                          </Stack>
                        )}

                        {promo.type === 'speed_upgrade' && (
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Aumento de Velocidad:</Typography>
                              <Typography variant="subtitle2" color="primary.main" fontWeight="bold">x {parseFloat(promo.speed_upgrade_multiplier || 0).toFixed(1)} (Doble)</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Costo Adicional:</Typography>
                              <Typography variant="subtitle2" color="textPrimary" fontWeight="bold">S/ {parseFloat(promo.speed_upgrade_price_addon || 0).toFixed(2)}/mes</Typography>
                            </Stack>
                          </Stack>
                        )}

                        {promo.type === 'free_service' && (
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Servicio Regalo:</Typography>
                              <Typography variant="subtitle2" color="warning.main" fontWeight="bold">{promo.service_name || 'Streaming'}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Costo para Cliente:</Typography>
                              <Typography variant="subtitle2" color="success.main" fontWeight="bold">GRATIS S/ 0.00</Typography>
                            </Stack>
                          </Stack>
                        )}

                        {promo.type === 'speed_and_free_service' && (
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Aumento de Velocidad:</Typography>
                              <Typography variant="subtitle2" color="primary.main" fontWeight="bold">x {parseFloat(promo.speed_upgrade_multiplier || 0).toFixed(1)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Costo Adicional:</Typography>
                              <Typography variant="subtitle2" color="textPrimary" fontWeight="bold">S/ {parseFloat(promo.speed_upgrade_price_addon || 0).toFixed(2)}/mes</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Servicio Regalo:</Typography>
                              <Typography variant="subtitle2" color="warning.main" fontWeight="bold">{promo.service_name || 'Streaming'}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Costo Servicio Regalo:</Typography>
                              <Typography variant="subtitle2" color="success.main" fontWeight="bold">GRATIS S/ 0.00</Typography>
                            </Stack>
                          </Stack>
                        )}

                        {promo.type === 'plan_upgrade_with_discount_and_free_service' && (() => {
                          const planPrice = parseFloat(promo.target_plan_price || 0);
                          const servicePrice = parseFloat(promo.service_price || 0);
                          const realTotal = planPrice + servicePrice;
                          const discount = parseFloat(promo.discount_amount || 0);
                          const promoPrice = Math.max(0, realTotal - discount);
                          return (
                            <Stack spacing={0.8}>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">Plan:</Typography>
                                <Typography variant="subtitle2" color="textPrimary" fontWeight="bold">
                                  {promo.target_plan_name || 'Plan Superior'} - S/ {planPrice.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">Servicio Adicional:</Typography>
                                <Typography variant="subtitle2" color="textPrimary" fontWeight="bold">
                                  {promo.service_name || 'Servicio Regalo'} - S/ {servicePrice.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Divider sx={{ my: 0.5 }} />
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">Precio Real del Servicio:</Typography>
                                <Typography variant="subtitle2" color="textSecondary" fontWeight="bold" sx={{ textDecoration: 'line-through' }}>
                                  S/ {realTotal.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">Precio Total de Promoción:</Typography>
                                <Typography variant="subtitle2" color="success.main" fontWeight="800">
                                  S/ {promoPrice.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">Duración de la Promoción:</Typography>
                                <Typography variant="subtitle2" color="textPrimary" fontWeight="bold">
                                  {promo.discount_months} meses
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5, textAlign: 'right' }}>
                                Después se cobrará el precio real de S/ {realTotal.toFixed(2)}/mes
                              </Typography>
                            </Stack>
                          );
                        })()}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPromo(promo);
                            setOpenForm(true);
                          }}
                          sx={{ color: '#6366f1', bgcolor: 'rgba(99, 102, 241, 0.06)', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.12)' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPromoToDelete(promo);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.06)', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.12)' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Tab content 2: Eligible Clients */}
      {tabIndex === 1 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, bgcolor: 'white' }}>
                <InputLabel>Seleccione la Campaña para Consultar Elegibles</InputLabel>
                <Select
                  value={selectedPromoForEligible}
                  onChange={(e) => setSelectedPromoForEligible(e.target.value)}
                  label="Seleccione la Campaña para Consultar Elegibles"
                >
                  {promotions.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} ({p.type === 'discount' ? 'Descuento' : p.type === 'speed_upgrade' ? 'Velocidad' : p.type === 'speed_and_free_service' ? 'Velocidad + Streaming' : p.type === 'plan_upgrade_with_discount_and_free_service' ? 'Upgrade + Desc + Streaming' : 'Streaming'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loadingEligible ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : eligibleClients.length === 0 ? (
            <Paper sx={{ p: 6, borderRadius: '20px', textAlign: 'center', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
              <AutoAwesomeIcon sx={{ fontSize: 54, color: '#6366f1', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold">Sin nuevos candidatos</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                No hay clientes que cumplan los requisitos de puntualidad requeridos para esta promoción que no la tengan ya asignada.
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  {selectedClients.length} de {eligibleClients.length} clientes seleccionados
                </Typography>
                <Button
                  variant="contained"
                  disabled={selectedClients.length === 0}
                  onClick={() => setOpenOfferDialog(true)}
                  startIcon={<GiftIcon />}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 700,
                    backgroundColor: '#10b981',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#059669' }
                  }}
                >
                  Ofrecer Promoción
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedClients.length > 0 && selectedClients.length < eligibleClients.length}
                          checked={eligibleClients.length > 0 && selectedClients.length === eligibleClients.length}
                          onChange={handleSelectAllClients}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Celular</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>IP</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Estado Servicio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eligibleClients.map((client) => {
                      const isSelected = selectedClients.some(c => c.id === client.id);
                      return (
                        <TableRow key={client.id} hover onClick={() => handleClientSelect(client)} sx={{ cursor: 'pointer' }}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={isSelected} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {client.first_name} {client.last_name}
                          </TableCell>
                          <TableCell sx={{ color: '#475569' }}>{client.email || 'No registrado'}</TableCell>
                          <TableCell sx={{ color: '#475569' }}>{client.phone || 'No registrado'}</TableCell>
                          <TableCell sx={{ color: '#0f172a', fontFamily: 'monospace' }}>{client.ip_address}</TableCell>
                          <TableCell>
                            <Chip
                              label={client.status === 'active' ? 'Activo' : 'Suspendido'}
                              color={client.status === 'active' ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 700, borderRadius: '6px' }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Tab content 3: Active Client Promotions */}
      {tabIndex === 2 && (
        <Box>
          {loadingActiveClientPromos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : activeClientPromos.length === 0 ? (
            <Paper sx={{ p: 6, borderRadius: '20px', textAlign: 'center', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
              <StarIcon sx={{ fontSize: 54, color: '#94a3b8', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold">No hay promociones activadas aún</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Cuando los clientes reclamen o se les active una promoción, figurarán en este listado.
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Promoción Activada</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Tipo de Beneficio</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Fecha Activación</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeClientPromos.map((cp) => (
                      <TableRow key={cp.client_promotion_id} hover>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {cp.first_name} {cp.last_name}
                          {cp.phone && <Typography variant="caption" display="block" color="textSecondary">{cp.phone}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6366f1' }}>
                            {cp.promotion_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {cp.promotion_description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              cp.promotion_type === 'discount' ? 'Descuento' :
                              cp.promotion_type === 'speed_upgrade' ? 'Doble Velocidad' :
                              cp.promotion_type === 'speed_and_free_service' ? 'Velocidad + Streaming' :
                              cp.promotion_type === 'plan_upgrade_with_discount_and_free_service' ? 'Upgrade + Descuento + Streaming' : 'Servicio Gratis'
                            }
                            color={
                              cp.promotion_type === 'discount' ? 'success' :
                              cp.promotion_type === 'speed_upgrade' ? 'primary' :
                              cp.promotion_type === 'speed_and_free_service' ? 'info' :
                              cp.promotion_type === 'plan_upgrade_with_discount_and_free_service' ? 'secondary' : 'warning'
                            }
                            size="small"
                            sx={{ fontWeight: 700, borderRadius: '6px' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#475569', fontWeight: 600 }}>
                          {cp.activated_at ? new Date(cp.activated_at).toLocaleDateString() : (cp.offered_at ? new Date(cp.offered_at).toLocaleDateString() : 'N/A')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cp.client_promotion_status === 'active' ? 'ACTIVO' : cp.client_promotion_status === 'claimed' ? 'RECLAMADO' : 'OFRECIDO'}
                            color={cp.client_promotion_status === 'active' ? 'success' : cp.client_promotion_status === 'claimed' ? 'info' : 'warning'}
                            size="small"
                            sx={{ fontWeight: 800, borderRadius: '6px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px', fontWeight: 700 }}
                            onClick={async () => {
                              if (window.confirm(`¿Deseas revocar/desactivar la promoción para ${cp.first_name} ${cp.last_name}?`)) {
                                await fetch(`/api/promotions/client-promotions/${cp.client_promotion_id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                });
                                fetchActiveClientPromos();
                              }
                            }}
                          >
                            Desactivar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Promotion Form Dialog */}
      <PromotionForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedPromo(null);
        }}
        onSubmit={handlePromoSubmit}
        billingServices={billingServices}
        internetPlans={internetPlans}
        promotion={selectedPromo}
      />

      {/* Offer Dialog */}
      <OfferDialog
        open={openOfferDialog}
        onClose={() => setOpenOfferDialog(false)}
        onSubmit={handleOfferPromo}
        selectedClients={selectedClients}
        promotion={promotions.find(p => p.id === selectedPromoForEligible)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Eliminar Promoción</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', fontWeight: 500 }}>
            ¿Estás seguro de que deseas eliminar la promoción "{promoToDelete?.name}"? Esta acción removerá el registro del catálogo.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button onClick={handleDeletePromo} color="error" variant="contained" sx={{ borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 700 }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Promotions;
